import { useContext, useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  ScrollRestoration,
  useLocation,
} from "react-router-dom";

import { useBmaSitesList, useCostSummary, useIntegrationsCount } from "../api";
import { useOverdueOnMe } from "../api/hooks/useDashboardActions";
import { useUnreadAnswers } from "../api/hooks/useUnreadAnswers";
import { useOpenFeedback } from "../api/hooks/useAgentFeedback";
import { useQuestionsWaiting } from "../api/hooks/useStatefulAgents";
import { UserContext } from "../auth";
import { AmazonMark } from "../components/AmazonMark";
import { OVERVIEW_PATH } from "../routes/amazonOverview/overviewPages";
import { isDemoMode } from "../demo/demoMode";
import { AgentPaneProvider } from "../state/AgentPaneContext";
import { UploadSubmitProvider } from "../state/UploadSubmitContext";
import { UploadStatusPill } from "../state/UploadStatusPill";
import { AgentPane } from "../components/AgentPane";
import { CountBadge } from "../components/CountBadge";
import { NewVersionBanner } from "../components/NewVersionBanner";
import { ReconnectBanner } from "../components/ReconnectBanner";
import { Tooltip } from "../components/Tooltip";
import { formatUsd } from "../utils/estimateRunCost";
import { unpricedMessage } from "../utils/unpricedMessage";
import { useDeferredReady } from "../utils/useDeferredReady";
import { useIsMobile } from "../utils/useIsMobile";
import { useVisibleViewport } from "../utils/useVisibleViewport";
import { OverviewNav } from "../routes/amazonOverview/OverviewNav";
import { NavIcon, type NavIconName } from "./NavIcon";
import styles from "./AppLayout.module.css";

interface NavItem {
  id: string;
  label: string;
  // Only items with an icon appear in the collapsed rail.
  icon?: NavIconName;
  to: string;
  disabled?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", icon: "dashboard", label: "Dashboard", to: "/dashboard" },
  { id: "projects", icon: "projects", label: "Projects", to: "/projects" },
  {
    id: "agents",
    icon: "agents",
    label: "Agents",
    to: "/agents",
  },
  {
    id: "global-data",
    icon: "global-data",
    label: "Global data",
    to: "/global-data",
  },
  { id: "my-actions", icon: "my-actions", label: "My actions", to: "/my-actions" },
];

const NAV_COLLAPSED_KEY = "inrange-agents.nav.collapsed";

// A per-viewer convenience: unreadable storage just means expanded.
const storedNavCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

// Inner layout body - lives inside UploadSubmitProvider so it can render
// the floating upload pill. Split from `AppLayout` so the provider can wrap it.
const AppLayoutBody = () => {
  const { pathname } = useLocation();
  const { user, logout } = useContext(UserContext);
  // Demo mode hides costs + the real user identity (see docs/Y2026-Q2/design-demo-mode.md).
  const demo = isDemoMode();

  // Below 768px the sidebar collapses into an off-canvas drawer toggled
  // from the top bar hamburger (mirrors the admin app's collapsing
  // Navbar). navOpen drives the slide-in; we close it on every route
  // change so following a nav link doesn't leave the drawer covering the
  // page the operator just navigated to.
  const isMobile = useIsMobile();
  // Once, for every page's pinned composer bar.
  useVisibleViewport();
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [pathname]);
  // Leaving mobile width (e.g. device rotate / window resize to desktop)
  // discards any open-drawer state so the desktop sidebar isn't stuck
  // behind a stale backdrop.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  // Desktop-only: mobile already hides the sidebar behind the drawer.
  const [navCollapsed, setNavCollapsed] = useState(storedNavCollapsed);
  const desktopCollapsed = !isMobile && navCollapsed;
  const toggleNavCollapsed = () => {
    const next = !navCollapsed;
    setNavCollapsed(next);
    try {
      window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      // Storage unavailable: the toggle still works for this page load.
    }
  };

  // These three shell-level reads (cost badge, BMA-sites prefetch,
  // and ReconnectBanner's auth-status) aren't needed to render the
  // current page, so we defer them off the initial request burst.
  // That keeps a page load's critical path to the route's own data
  // call - one Lambda container covers it rather than four cold
  // starts firing in parallel.
  const deferred = useDeferredReady();

  // Trailing-30-day Bedrock spend, backend-computed. The badge reflects
  // recent cost at a glance rather than an ever-incrementing all-time
  // counter. ``total_usd`` is the authoritative figure from
  // /cost/summary - the FE never sums cost client-side.
  const costQuery = useCostSummary({ days: 30 }, { enabled: deferred });
  const spend30dUsd = costQuery.data?.total_usd ?? null;
  const spendFormatted = formatUsd(spend30dUsd);
  const spendReady = !costQuery.isLoading || costQuery.data != null;
  // When the BE couldn't price some calls (unknown model), they're excluded
  // from total_usd - surface a ⚠ on the badge so the headline figure isn't a
  // silent under-count. Mirrors the warning on the /costs page summary.
  const unpricedCount = costQuery.data?.unpriced_count ?? 0;
  const unknownModels = costQuery.data?.unknown_models ?? [];

  // Outstanding (queued + running) third-party operations across the platform.
  // Polled, like the spend figure; the badge sits just above the spend badge and
  // links to /integrations. ``null`` until the first poll lands ("…"). Deferred
  // off the initial page-load burst (a non-critical navbar read, like the spend
  // badge above) so it doesn't compete with page content.
  const integrationsCountQuery = useIntegrationsCount({ enabled: deferred });
  const outstandingOps = integrationsCountQuery.data?.outstanding ?? null;
  const outstandingReady =
    !integrationsCountQuery.isLoading || integrationsCountQuery.data != null;

  // Warm the BMA sites list (the heaviest shell call) so the New Project
  // picker reads it from cache instead of waiting on the GET
  // /bma/sites/list round-trip. Deferred off the initial burst (it's
  // pure prefetch, not page content); NewProjectRoute's own
  // useBmaSitesList still fetches on demand if the operator gets there
  // first, sharing the same 5-min React Query cache.
  useBmaSitesList({ enabled: deferred });

  // Deferred like the reads above: a nav badge, not page content.
  const questionsWaiting = useQuestionsWaiting({ enabled: deferred }).total;
  // The viewer's own overdue actions only, in red: a teammate's are theirs to see.
  const overdueOnMe =
    useOverdueOnMe(user?.email ?? "", deferred && !demo).data?.overdue ?? 0;
  // Then in blue: agent questions waiting on an operator that are the viewer's or nobody's,
  // as Open agent feedback opens on them.
  const openFeedback = useOpenFeedback(
    deferred && !demo,
    (user?.email ?? "").toLowerCase(),
  ).waiting;
  // And in blue unfilled: replies to the viewer they have not opened since.
  const unreadAnswers =
    useUnreadAnswers(user?.email ?? "", deferred && !demo).data?.unread
      .length ?? 0;
  // ``rail``: pinned to the collapsed rail's icon, My actions' counts stacked down its edge.
  const navBadge = (id: string, rail: boolean) => {
    if (id === "agents")
      return (
        <CountBadge
          count={questionsWaiting}
          className={rail ? styles.railBadge : undefined}
        />
      );
    if (id !== "my-actions") return null;
    const one = rail ? styles.railStackBadge : undefined;
    const overdue = (
      <CountBadge
        count={overdueOnMe}
        className={`${styles.overdueBadge} ${rail ? styles.railBadgeLeft : ""}`}
      />
    );
    const blues = (
      <>
        <CountBadge count={openFeedback} className={one} />
        <CountBadge count={unreadAnswers} outline className={one} />
      </>
    );
    // On the rail the red sits on the icon's left corner, the blues down its right edge.
    return rail ? (
      <>
        {overdue}
        <span className={styles.railStack}>{blues}</span>
      </>
    ) : (
      <>
        {overdue}
        {blues}
      </>
    );
  };

  const showNav = !pathname.startsWith("/amazon-overview");

  return (
    <>
      {/* The Amazon overview is shown to Amazon, so its nav has only its own pages, and
          for InRange alone a way back here (Noaa, 22 Oct 2026). */}
      {!showNav && <OverviewNav />}
      {showNav && (
        <>
          {isMobile && navOpen && (
            <div
              className={styles.backdrop}
              onClick={() => setNavOpen(false)}
              aria-hidden
            />
          )}
          {desktopCollapsed && (
            <aside className={styles.rail}>
              <button
                type="button"
                className={styles.menuButton}
                onClick={toggleNavCollapsed}
                aria-label="Expand navigation"
                aria-expanded={false}
                title="Expand navigation"
              >
                <span className={styles.menuIcon} aria-hidden>
                  <span />
                  <span />
                  <span />
                </span>
              </button>
              <Tooltip content="New project" placement="bottom" align="left">
                <Link
                  to="/new"
                  className={styles.railNewProject}
                  aria-label="New project"
                >
                  <NavIcon name="plus" />
                </Link>
              </Tooltip>
              <nav className={styles.railNav}>
                {NAV_ITEMS.flatMap((it) =>
                  it.icon
                    ? [
                        <Tooltip
                          key={it.id}
                          content={it.label}
                          placement="bottom"
                          align="left"
                        >
                          <NavLink
                            to={it.to}
                            aria-label={it.label}
                            className={({ isActive }) =>
                              `${styles.railItem} ${isActive ? styles.itemActive : ""}`
                            }
                          >
                            <NavIcon name={it.icon} />
                            {navBadge(it.id, true)}
                          </NavLink>
                        </Tooltip>,
                      ]
                    : [],
                )}
              </nav>
              <div className={styles.railFoot}>
                <Tooltip content="Amazon overview" placement="top" align="left">
                  <Link
                    to={OVERVIEW_PATH}
                    aria-label="Amazon overview"
                    className={styles.railSwitch}
                  >
                    <AmazonMark />
                  </Link>
                </Tooltip>
              </div>
            </aside>
          )}
          <aside
            className={`${styles.sidebar} ${
              isMobile && navOpen ? styles.sidebarOpen : ""
            }`}
            hidden={desktopCollapsed}
          >
            <div className={styles.brandRow}>
              <div className={styles.brand}>InRange Agents</div>
              {!isMobile && (
                <button
                  type="button"
                  className={styles.collapseButton}
                  onClick={toggleNavCollapsed}
                  aria-label="Collapse navigation"
                  aria-expanded
                  title="Collapse navigation"
                >
                  <span aria-hidden>«</span>
                </button>
              )}
            </div>
            <Link to="/new" className={styles.newProjectBtn}>
              <span className={styles.newProjectPlus} aria-hidden>
                +
              </span>
              New project
            </Link>
            <nav className={styles.nav}>
              {NAV_ITEMS.map((it) => (
                <NavLink
                  key={it.id}
                  to={it.to}
                  className={({ isActive }) =>
                    `${styles.item} ${isActive ? styles.itemActive : ""}`
                  }
                >
                  <span className={styles.itemIcon} aria-hidden>
                    {it.icon && <NavIcon name={it.icon} />}
                  </span>
                  {it.label}
                  {navBadge(it.id, false)}
                </NavLink>
              ))}
            </nav>
            <div className={styles.foot}>
              {!demo && (
                <div className={styles.navStats}>
                  <Tooltip
                    placement="top"
                    align="left"
                    content="Third-party (Gmail + Drive) operations queued or running across the platform. Each Google account runs one at a time; the rest queue. Click to open the Integrations page."
                  >
                    <Link to="/integrations" className={styles.spendBadge}>
                      <strong>
                        {outstandingReady ? (outstandingOps ?? 0) : "…"}
                      </strong>
                      <span>Integrations</span>
                      <span className={styles.spendSince}>
                        {outstandingOps === 1
                          ? "operation outstanding"
                          : "operations outstanding"}
                      </span>
                    </Link>
                  </Tooltip>
                  <Tooltip
                    placement="top"
                    align="left"
                    content="Estimated Bedrock spend over the trailing 30 days. USD, computed by the backend; the AWS invoice is the source of truth. Click to open the AI costs page."
                  >
                    <Link to="/costs" className={styles.spendBadge}>
                      <strong>
                        ~{spendReady ? spendFormatted : "…"}
                        {unpricedCount > 0 && (
                          <span className={styles.spendWarnIcon} aria-hidden>
                            {" "}
                            ⚠
                          </span>
                        )}
                      </strong>
                      <span>AI spend</span>
                      <span className={styles.spendSince}>last 30 days</span>
                    </Link>
                  </Tooltip>
                  {unpricedCount > 0 && (
                    <Tooltip
                      placement="top"
                      align="left"
                      content={unpricedMessage(unpricedCount, unknownModels)}
                    >
                      <Link
                        to="/costs"
                        className={styles.spendWarnBadge}
                        aria-label={unpricedMessage(unpricedCount, unknownModels)}
                      >
                        ⚠
                      </Link>
                    </Tooltip>
                  )}
                </div>
              )}
              <Link to={OVERVIEW_PATH} className={styles.appSwitch}>
                <AmazonMark />
                Amazon overview
              </Link>
              <div>{demo ? "Demo User" : (user?.email ?? "Signed in")}</div>
              {logout && (
                <button
                  type="button"
                  onClick={() => logout()}
                  className={styles.logoutButton}
                >
                  Log out
                </button>
              )}
            </div>
          </aside>
          {isMobile && (
            <button
              type="button"
              className={`${styles.menuButton} ${styles.floatingMenuButton}`}
              onClick={() => setNavOpen((open) => !open)}
              aria-label={navOpen ? "Close menu" : "Open menu"}
              aria-expanded={navOpen}
            >
              <span className={styles.menuIcon} aria-hidden>
                <span />
                <span />
                <span />
              </span>
            </button>
          )}
        </>
      )}
      <main className={styles.main}>
        <NewVersionBanner offerAvailable />
        <ReconnectBanner />
        <div className={styles.scroll}>
          <Outlet />
        </div>
      </main>
      <AgentPane />
      <UploadStatusPill />
      {/* A new page opens at its top, and Back returns to where the reader was. */}
      <ScrollRestoration />
    </>
  );
};

export const AppLayout = () => (
  <UploadSubmitProvider>
    <AgentPaneProvider>
      <AppLayoutBody />
    </AgentPaneProvider>
  </UploadSubmitProvider>
);
